import { t, locale } from "../i18n";
function hexToRgba(hex, alpha) {
  if (!hex) return `rgba(0,0,0,${alpha})`;
  if (hex.startsWith("rgb")) return hex;
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

export function containerStyle(el) {
  return {
    position: "absolute",
    left: el.x,
    top: el.y,
    width: el.largura,
    height: el.altura,
    transform: `rotate(${el.rotacao || 0}deg)`,
    opacity: (el.opacidade ?? 100) / 100,
    zIndex: el.zIndex || 0,
    mixBlendMode: el.blendMode || "normal",
  };
}

export function textInnerStyle(s = {}) {
  const bg = hexToRgba(s.bgColor || "#000000", (s.bgOpacity ?? 0) / 100);
  const vAlign =
    s.verticalAlign === "top" ? "flex-start" : s.verticalAlign === "bottom" ? "flex-end" : "center";
  return {
    width: "100%",
    height: "100%",
    boxSizing: "border-box",
    fontFamily: `'${s.fontFamily || "Manrope"}', sans-serif`,
    fontSize: `${s.fontSize || 72}px`,
    color: s.color || "#ffffff",
    fontWeight: s.fontWeight || 700,
    textAlign: s.textAlign || "left",
    lineHeight: s.lineHeight || 1.2,
    letterSpacing: `${s.letterSpacing || 0}px`,
    background: bg,
    padding: `${s.padding || 0}px`,
    borderRadius: `${s.borderRadius || 0}px`,
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
    display: "flex",
    flexDirection: "column",
    justifyContent: vAlign,
    overflow: "hidden",
    WebkitTextStroke: s.strokeWidth ? `${s.strokeWidth}px ${s.strokeColor || "#000000"}` : undefined,
    textShadow:
      s.shadowBlur || s.shadowX || s.shadowY
        ? `${s.shadowX || 0}px ${s.shadowY || 0}px ${s.shadowBlur || 0}px ${s.shadowColor || "rgba(0,0,0,0.6)"}`
        : undefined,
  };
}

// ---------------------------------------------------------------------------
// Animation model: { entrada: {efeito, direcao, duracao, delay}, saida: {...} }
// efeito: none | fade | slide | scale ; direcao: up | down | left | right
// Backward compatible with the legacy string form (entrada: "fadeIn").
// ---------------------------------------------------------------------------

export const EFEITOS = [
  { value: "none", label: t("Nenhum") },
  { value: "fade", label: "Fade" },
  { value: "slide", label: "Slide" },
  { value: "scale", label: "Zoom" },
  { value: "bounce", label: t("Salto (bounce)") },
  { value: "flip", label: "Flip 3D" },
  { value: "blur", label: t("Desfoque") },
  { value: "wipe", label: t("Revelar (wipe)") },
];
export const DIRECOES = [
  { value: "up", label: t("Cima") },
  { value: "down", label: t("Baixo") },
  { value: "left", label: t("Esquerda") },
  { value: "right", label: t("Direita") },
];
// 15 fontes mais usadas do Google Fonts, disponíveis para todos os usuários (pesos que cada uma oferece).
export const GOOGLE_FONTS = {
  "Roboto": "400;500;700;900",
  "Open Sans": "400;600;700;800",
  "Montserrat": "400;500;600;700;800;900",
  "Inter": "400;500;600;700;800;900",
  "Poppins": "400;500;600;700;800;900",
  "Lato": "400;700;900",
  "Roboto Condensed": "400;500;700",
  "Oswald": "400;500;600;700",
  "Noto Sans": "400;500;700",
  "Raleway": "400;500;600;700;800",
  "Nunito": "400;600;700;800;900",
  "Playfair Display": "400;600;700;900",
  "Ubuntu": "400;500;700",
  "Rubik": "400;500;600;700;800",
  "Merriweather": "400;700;900",
};
/** URL do Google Fonts para as famílias pedidas (só as que estão na lista). */
export function googleFontsUrl(families) {
  const fams = [...new Set(families)].filter((f) => GOOGLE_FONTS[f]);
  if (!fams.length) return null;
  return "https://fonts.googleapis.com/css2?" + fams.map((f) => `family=${f.replace(/ /g, "+")}:wght@${GOOGLE_FONTS[f]}`).join("&") + "&display=swap";
}
export const FONTS = ["Manrope", ...Object.keys(GOOGLE_FONTS).sort((a, b) => a.localeCompare(b)), "IBM Plex Sans", "JetBrains Mono", "Georgia", "Arial", "Impact", "Courier New"];

function parseLegacy(name) {
  switch (name) {
    case "fadeIn": return { efeito: "fade", direcao: "up" };
    case "scaleIn": return { efeito: "scale", direcao: "up" };
    case "slideUp": return { efeito: "slide", direcao: "up" };
    case "slideDown": return { efeito: "slide", direcao: "down" };
    case "slideLeft": return { efeito: "slide", direcao: "left" };
    case "slideRight": return { efeito: "slide", direcao: "right" };
    default: return { efeito: "none", direcao: "up" };
  }
}

export function resolveAnim(animacao) {
  const defIn = { efeito: "none", direcao: "up", duracao: 500, delay: 0 };
  const defOut = { efeito: "none", direcao: "up", duracao: 400 };
  if (animacao && typeof animacao.entrada === "object") {
    return {
      entrada: { ...defIn, ...animacao.entrada },
      saida: { ...defOut, ...(animacao.saida || {}) },
    };
  }
  if (animacao) {
    const leg = parseLegacy(animacao.entrada);
    return {
      entrada: { ...defIn, ...leg, duracao: animacao.duracao || 500, delay: animacao.delay || 0 },
      saida: { ...defOut, ...(animacao.saida || {}) },
    };
  }
  return { entrada: { ...defIn }, saida: { ...defOut } };
}

export function animName(cfg, mode) {
  if (!cfg || !cfg.efeito || cfg.efeito === "none") return null;
  if (cfg.efeito === "fade") return mode === "in" ? "fadeIn" : "fadeOut";
  if (cfg.efeito === "scale") return mode === "in" ? "scaleIn" : "scaleOut";
  if (cfg.efeito === "bounce") return mode === "in" ? "bounceIn" : "bounceOut";
  if (cfg.efeito === "blur") return mode === "in" ? "blurIn" : "blurOut";
  if (cfg.efeito === "flip") {
    const d = cfg.direcao || "up";
    const axis = d === "left" || d === "right" ? "Y" : "X";
    return mode === "in" ? `flip${axis}In` : `flip${axis}Out`;
  }
  if (cfg.efeito === "slide" || cfg.efeito === "wipe") {
    const prefix = cfg.efeito;
    const d = cfg.direcao || "up";
    const C = d[0].toUpperCase() + d.slice(1);
    return mode === "in" ? `${prefix}${C}In` : `${prefix}${C}Out`;
  }
  return null;
}

export function enterAnimCss(el) {
  const { entrada } = resolveAnim(el.animacao);
  const name = animName(entrada, "in");
  if (!name) return {};
  return {
    animationName: name,
    animationDuration: `${entrada.duracao}ms`,
    animationTimingFunction: "ease",
    animationDelay: `${entrada.delay || 0}ms`,
    animationFillMode: "both",
  };
}

export function exitAnimCss(el) {
  const { saida } = resolveAnim(el.animacao);
  const name = animName(saida, "out");
  if (!name) return null;
  return {
    animationName: name,
    animationDuration: `${saida.duracao}ms`,
    animationTimingFunction: "ease",
    animationFillMode: "both",
  };
}

export function defaultTextElement(zIndex) {
  return {
    id: crypto.randomUUID(),
    tipo: "texto",
    nome: t("Texto"),
    x: 660, y: 460, largura: 600, altura: 160,
    rotacao: 0, zIndex, opacidade: 100, visivel: true, travado: false,
    conteudo: t("Seu texto aqui"),
    blendMode: "normal",
    estilo: {
      fontFamily: "Manrope", fontSize: 72, color: "#ffffff", fontWeight: 800,
      textAlign: "left", verticalAlign: "middle", lineHeight: 1.2, letterSpacing: 0,
      strokeWidth: 0, strokeColor: "#000000", shadowX: 0, shadowY: 0, shadowBlur: 0,
      shadowColor: "rgba(0,0,0,0.7)", bgColor: "#000000", bgOpacity: 0, padding: 12, borderRadius: 8,
    },
    animacao: {
      entrada: { efeito: "fade", direcao: "up", duracao: 500, delay: 0 },
      saida: { efeito: "none", direcao: "up", duracao: 400 },
    },
  };
}

export function defaultMediaElement(media, zIndex) {
  const w = Math.min(media.largura || 640, 960);
  const ratio = media.altura && media.largura ? media.altura / media.largura : 0.5625;
  return {
    id: crypto.randomUUID(),
    tipo: "media",
    nome: media.nome_original,
    mediaId: media.id,
    x: 100, y: 100, largura: w, altura: Math.round(w * ratio),
    rotacao: 0, zIndex, opacidade: 100, visivel: true, travado: false,
    loop: true, autoplay: true, blendMode: "normal",
    animacao: {
      entrada: { efeito: "fade", direcao: "up", duracao: 500, delay: 0 },
      saida: { efeito: "none", direcao: "up", duracao: 400 },
    },
  };
}


export function defaultShapeElement(zIndex, variante = "circulo") {
  const nomes = { circulo: t("Círculo"), triangulo: t("Triângulo"), estrela: t("Estrela"), retangulo: t("Retângulo"), losango: t("Losango") };
  return {
    id: crypto.randomUUID(),
    tipo: "forma",
    variante,
    nome: nomes[variante] || t("Forma"),
    x: 820, y: 440, largura: 280, altura: 280,
    rotacao: 0, zIndex, opacidade: 100, visivel: true, travado: false, blendMode: "normal",
    estilo: { fillColor: "#007AFF", strokeColor: "#000000", strokeWidth: 0, shadowEnabled: false, shadowColor: "#000000", shadowOpacity: 50, shadowX: 0, shadowY: 0, shadowBlur: 0 },
    animacao: {
      entrada: { efeito: "fade", direcao: "up", duracao: 500, delay: 0 },
      saida: { efeito: "none", direcao: "up", duracao: 400 },
    },
  };
}

// ---------------------------------------------------------------------------
// Dynamic YouTube metric elements
// ---------------------------------------------------------------------------

export const METRICAS = [
  { value: "viewers", label: t("Espectadores ao vivo") },
  { value: "likes", label: t("Curtidas") },
  { value: "views", label: t("Visualizações") },
  { value: "subscribers", label: t("Inscritos") },
  { value: "duration", label: t("Duração ao vivo") },
];

export const METRICAS_TWITCH = [
  { value: "viewers", label: t("Espectadores ao vivo") },
  { value: "followers", label: t("Seguidores") },
];

export function abbrevNum(n) {
  const num = Number(n) || 0;
  if (num < 1000) return String(num);
  if (num < 1_000_000) {
    const v = num / 1000;
    return (v >= 100 ? v.toFixed(0) : v.toFixed(1)).replace(/\.0$/, "") + "k";
  }
  const v = num / 1_000_000;
  return (v >= 100 ? v.toFixed(0) : v.toFixed(1)).replace(/\.0$/, "") + "M";
}

export function fmtDuration(sec) {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  const pad = (x) => String(x).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(ss)}` : `${pad(m)}:${pad(ss)}`;
}

export function metricRaw(el, ytData) {
  const d = ytData || {};
  switch (el.metrica) {
    case "viewers": return d.viewers || 0;
    case "likes": return d.likes || 0;
    case "views": return d.views || 0;
    case "subscribers": return d.subscribers || 0;
    case "followers": return d.followers || 0;
    default: return 0;
  }
}

export function formatMetricValue(el, ytData, liveSeconds) {
  if (el.metrica === "duration") return fmtDuration(liveSeconds || 0);
  const raw = metricRaw(el, ytData);
  return el.formato === "abbrev" ? abbrevNum(raw) : Number(raw).toLocaleString(locale());
}

export function applyTemplate(tpl, val) {
  return String(tpl ?? "{n}").split("{n}").join(val);
}

export function defaultDynamicElement(zIndex, metrica = "viewers") {
  return {
    id: crypto.randomUUID(),
    tipo: "dynamic",
    nome: t("Dado YouTube"),
    metrica,
    formato: "abbrev",
    modo: "texto",
    template: "{n} assistindo",
    meta: 1000,
    x: 80, y: 80, largura: 460, altura: 120,
    rotacao: 0, zIndex, opacidade: 100, visivel: true, travado: false,
    blendMode: "normal",
    estilo: {
      fontFamily: "Manrope", fontSize: 64, color: "#ffffff", fontWeight: 800,
      textAlign: "left", verticalAlign: "middle", lineHeight: 1.1, letterSpacing: 0,
      strokeWidth: 0, strokeColor: "#000000", shadowX: 0, shadowY: 0, shadowBlur: 0,
      shadowColor: "rgba(0,0,0,0.7)", bgColor: "#000000", bgOpacity: 0, padding: 12, borderRadius: 8,
      barColor: "#007AFF", barBg: "#1a1a20",
    },
    animacao: {
      entrada: { efeito: "fade", direcao: "up", duracao: 500, delay: 0 },
      saida: { efeito: "none", direcao: "up", duracao: 400 },
    },
  };
}

export function defaultTwitchElement(zIndex, metrica = "viewers") {
  const el = defaultDynamicElement(zIndex, metrica);
  return {
    ...el,
    nome: t("Dado Twitch"),
    platform: "twitch",
    estilo: { ...el.estilo, barColor: "#9146FF" },
  };
}

// ---------------------------------------------------------------------------
// Live chat elements (Fase 3)
// ---------------------------------------------------------------------------

function _chatBaseAnim() {
  return {
    entrada: { efeito: "fade", direcao: "up", duracao: 500, delay: 0 },
    saida: { efeito: "none", direcao: "up", duracao: 400 },
  };
}

export function defaultChatFeedElement(zIndex) {
  return {
    id: crypto.randomUUID(),
    tipo: "chat", nome: t("Caixa de chat"),
    x: 60, y: 380, largura: 460, altura: 560,
    rotacao: 0, zIndex, opacidade: 100, visivel: true, travado: false, blendMode: "normal",
    maxMensagens: 8, mostrarAvatar: true,
    estilo: {
      fontFamily: "Manrope", fontSize: 26, color: "#ffffff", authorColor: "#7fb0ff",
      bgColor: "#0a0a0c", bgOpacity: 68, borderRadius: 14, padding: 16,
    },
    animacao: _chatBaseAnim(),
  };
}

export function defaultCommentElement(zIndex) {
  return {
    id: crypto.randomUUID(),
    tipo: "comment", nome: t("Comentário em destaque"),
    x: 560, y: 820, largura: 800, altura: 200,
    rotacao: 0, zIndex, opacidade: 100, visivel: true, travado: false, blendMode: "normal",
    estilo: {
      fontFamily: "Manrope", fontSize: 40, color: "#ffffff", authorColor: "#7fb0ff",
      bgColor: "#0a0a0c", bgOpacity: 90, borderRadius: 18, padding: 26,
    },
    animacao: {
      entrada: { efeito: "slide", direcao: "up", duracao: 450, delay: 0 },
      saida: { efeito: "fade", direcao: "up", duracao: 350 },
    },
  };
}

export function defaultSuperChatElement(zIndex) {
  return {
    id: crypto.randomUUID(),
    tipo: "superchat", nome: t("Alerta de Super Chat"),
    x: 640, y: 120, largura: 640, altura: 190,
    rotacao: 0, zIndex, opacidade: 100, visivel: true, travado: false, blendMode: "normal",
    duracao: 8,
    estilo: {
      fontFamily: "Manrope", fontSize: 34, color: "#ffffff",
      bgColor: "#1a73e8", bgOpacity: 100, borderRadius: 18, padding: 22,
    },
    animacao: _chatBaseAnim(),
  };
}
